(() => {
  const mobileViewport = window.matchMedia('(max-width: 1023px), (pointer: coarse)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  class NoyocoProductGalleryViewer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.surface = this.querySelector('[data-viewer-surface]');
      this.viewport = this.querySelector('[data-viewer-viewport]');
      this.frame = this.querySelector('[data-viewer-frame]');
      this.image = this.querySelector('[data-viewer-image]');
      this.media = this.querySelector('[data-viewer-media]');
      this.closeButton = this.querySelector('[data-viewer-close]');
      this.previousButton = this.querySelector('[data-viewer-previous]');
      this.nextButton = this.querySelector('[data-viewer-next]');
      this.indexNode = this.querySelector('[data-viewer-index]');
      this.totalNode = this.querySelector('[data-viewer-total]');
      this.liveNode = this.querySelector('[data-viewer-live]');
      this.items = [];
      this.index = 0;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.dragX = 0;
      this.gestureAxis = null;
      this.pointers = new Map();
      this.animating = false;
      this.transitionSequence = 0;
      this.historyEntry = false;
      this.lastTap = null;
      this.bindEvents();
    }

    bindEvents() {
      this.closeButton.addEventListener('click', () => this.close());
      this.previousButton.addEventListener('click', () => this.go(-1));
      this.nextButton.addEventListener('click', () => this.go(1));
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', event => this.onKeydown(event));
      this.viewport.addEventListener('pointerdown', event => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', event => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', event => this.onPointerEnd(event));
      this.viewport.addEventListener('pointercancel', event => this.onPointerCancel(event));
      window.addEventListener('resize', () => {
        if (this.dialog.open) {
          this.clampTranslation();
          this.applyZoom();
        }
      });
      window.addEventListener('popstate', event => {
        if (this.dialog.open && !event.state?.noyocoProductViewer) {
          this.historyEntry = false;
          this.close({ fromHistory: true });
          return;
        }

        if (!this.dialog.open && event.state?.noyocoProductViewer && this.items.length) {
          this.historyEntry = true;
          this.render(clamp(event.state.index || 0, 0, this.items.length - 1), false);
          document.documentElement.classList.add('noyoco-product-viewer-open');
          this.dialog.showModal();
          this.closeButton.focus({ preventScroll: true });
        }
      });
      mobileViewport.addEventListener('change', event => {
        if (!event.matches && this.dialog.open) this.close();
      });
    }

    collectItems(gallery) {
      return [...gallery.querySelectorAll(':scope > li:not(.hidden):not(.splide__slide--clone) > a')]
        .filter(anchor => anchor.closest('li')?.offsetParent !== null)
        .map(anchor => {
          const video = anchor.querySelector('video');
          const iframe = anchor.querySelector('iframe');
          const image = anchor.querySelector('img');
          let type = 'image';
          if (video) type = 'video';
          if (iframe) type = 'external_video';
          return {
            anchor,
            type,
            source: video || iframe || image,
            src: anchor.href,
            alt: image?.alt || ''
          };
        });
    }

    open(anchor, gallery) {
      if (!mobileViewport.matches || this.dialog.open) return;
      this.items = this.collectItems(gallery);
      this.index = this.items.findIndex(item => item.anchor === anchor);
      if (this.index < 0) this.index = 0;
      if (!this.items.length) return;
      this.opener = anchor;
      this.render(this.index, false);
      document.documentElement.classList.add('noyoco-product-viewer-open');
      this.dialog.showModal();
      this.closeButton.focus({ preventScroll: true });
      history.pushState({ noyocoProductViewer: true, index: this.index }, '', window.location.href);
      this.historyEntry = true;
    }

    close(options = {}) {
      if (!this.dialog.open) return;
      this.transitionSequence += 1;
      this.frame.getAnimations?.().forEach(animation => animation.cancel());
      this.surface.getAnimations?.().forEach(animation => animation.cancel());
      this.pointers.forEach((_, pointerId) => this.viewport.releasePointerCapture?.(pointerId));
      this.pointers.clear();
      this.gestureStart = null;
      this.gestureAxis = null;
      this.pinchStart = null;
      this.lastTap = null;
      this.animating = false;
      this.clearMedia();
      this.resetZoom();
      this.resetGestureStyles();
      this.dialog.close();
      document.documentElement.classList.remove('noyoco-product-viewer-open');
      if (this.historyEntry && !options.fromHistory) {
        this.historyEntry = false;
        history.back();
      }
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    render(index, announce = true) {
      const item = this.items[index];
      if (!item) return;
      this.index = index;
      this.resetZoom();
      this.resetGestureStyles();
      this.clearMedia();
      this.image.classList.remove('is-ready');
      if (item.type === 'image') {
        this.media.hidden = true;
        this.image.hidden = false;
        this.image.alt = item.alt;
        this.image.onload = () => {
          this.clampTranslation();
          this.applyZoom();
          this.image.classList.add('is-ready');
        };
        this.image.src = item.src;
        if (this.image.complete && this.image.naturalWidth) this.image.onload();
      } else {
        this.image.hidden = true;
        this.media.hidden = false;
        const mediaNode = item.source.cloneNode(true);
        mediaNode.removeAttribute('class');
        if (mediaNode instanceof HTMLVideoElement) {
          mediaNode.controls = true;
          mediaNode.autoplay = true;
          mediaNode.muted = true;
          mediaNode.loop = true;
          mediaNode.playsInline = true;
          mediaNode.play().catch(() => {});
        }
        if (mediaNode instanceof HTMLIFrameElement && !mediaNode.title) {
          mediaNode.title = item.alt || this.dataset.galleryLabel;
        }
        this.media.appendChild(mediaNode);
      }
      this.indexNode.textContent = String(index + 1);
      this.totalNode.textContent = String(this.items.length);
      if (this.historyEntry && this.dialog.open) {
        history.replaceState({ noyocoProductViewer: true, index }, '', window.location.href);
      }
      this.previousButton.disabled = index === 0;
      this.nextButton.disabled = index === this.items.length - 1;
      this.preloadNeighbour(index - 1);
      this.preloadNeighbour(index + 1);
      if (announce) {
        this.liveNode.textContent = this.dataset.liveLabel.replace('__index__', `${index + 1} / ${this.items.length}`);
      }
    }

    preloadNeighbour(index) {
      const item = this.items[index];
      if (!item || item.type !== 'image') return;
      const preload = new Image();
      preload.src = item.src;
    }

    clearMedia() {
      const video = this.media.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      this.media.replaceChildren();
    }

    async go(delta) {
      if (this.animating || this.scale > 1.05) return;
      const nextIndex = clamp(this.index + delta, 0, this.items.length - 1);
      if (nextIndex === this.index) {
        this.animateFrameBack();
        return;
      }
      if (reducedMotion.matches || !this.frame.animate) {
        this.render(nextIndex);
        return;
      }
      this.animating = true;
      const transitionSequence = ++this.transitionSequence;
      const direction = delta > 0 ? -1 : 1;
      const width = this.viewport.clientWidth;
      const outgoing = this.frame.animate(
        [
          { transform: `translate3d(${this.dragX}px, 0, 0)`, opacity: 1 },
          { transform: `translate3d(${direction * width}px, 0, 0)`, opacity: .55 }
        ],
        { duration: 180, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'forwards' }
      );
      await outgoing.finished.catch(() => {});
      if (transitionSequence !== this.transitionSequence || !this.dialog.open) {
        outgoing.cancel();
        return;
      }
      outgoing.cancel();
      this.render(nextIndex, false);
      const incoming = this.frame.animate(
        [
          { transform: `translate3d(${-direction * width}px, 0, 0)`, opacity: .55 },
          { transform: 'translate3d(0, 0, 0)', opacity: 1 }
        ],
        { duration: 220, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );
      await incoming.finished.catch(() => {});
      if (transitionSequence !== this.transitionSequence || !this.dialog.open) {
        incoming.cancel();
        return;
      }
      incoming.cancel();
      this.animating = false;
      this.dragX = 0;
      this.liveNode.textContent = this.dataset.liveLabel.replace('__index__', `${nextIndex + 1} / ${this.items.length}`);
    }

    onKeydown(event) {
      if (!this.dialog.open) return;
      if (event.target.closest?.('video, iframe, input, select, textarea')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
      if (event.key === 'Home') {
        event.preventDefault();
        this.go(-this.items.length);
      }
      if (event.key === 'End') {
        event.preventDefault();
        this.go(this.items.length);
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.zoomTo(this.scale + .5);
      }
      if (event.key === '-') {
        event.preventDefault();
        this.zoomTo(this.scale - .5);
      }
      if (event.key === '0') {
        event.preventDefault();
        this.zoomTo(1);
      }
    }

    onPointerDown(event) {
      if (this.animating || event.target.closest('button, video, iframe')) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.viewport.setPointerCapture?.(event.pointerId);
      if (this.pointers.size === 1) {
        this.gestureStart = {
          x: event.clientX,
          y: event.clientY,
          time: performance.now(),
          translateX: this.translateX,
          translateY: this.translateY
        };
        this.gestureAxis = null;
        this.dragX = 0;
      }
      if (this.pointers.size === 2 && !this.image.hidden) this.startPinch();
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size >= 2 && !this.image.hidden) {
        event.preventDefault();
        this.updatePinch();
        return;
      }
      if (!this.gestureStart) return;
      const deltaX = event.clientX - this.gestureStart.x;
      const deltaY = event.clientY - this.gestureStart.y;
      if (this.scale > 1.05 && !this.image.hidden) {
        event.preventDefault();
        this.translateX = this.gestureStart.translateX + deltaX;
        this.translateY = this.gestureStart.translateY + deltaY;
        this.clampTranslation();
        this.applyZoom();
        return;
      }
      if (!this.gestureAxis) {
        if (Math.hypot(deltaX, deltaY) < 12) return;
        if (Math.abs(deltaX) > Math.abs(deltaY) * 1.25) this.gestureAxis = 'horizontal';
        else if (deltaY < 0 && Math.abs(deltaY) > Math.abs(deltaX) * 1.25) this.gestureAxis = 'up';
        else this.gestureAxis = 'blocked';
      }
      if (this.gestureAxis === 'horizontal') {
        event.preventDefault();
        const atFirst = this.index === 0 && deltaX > 0;
        const atLast = this.index === this.items.length - 1 && deltaX < 0;
        this.dragX = atFirst || atLast ? deltaX * .22 : deltaX;
        this.frame.style.transform = `translate3d(${this.dragX}px, 0, 0)`;
      }
      if (this.gestureAxis === 'up') {
        event.preventDefault();
        const distance = Math.min(0, deltaY);
        this.surface.style.transform = `translate3d(0, ${distance}px, 0)`;
        this.surface.style.opacity = String(1 - Math.min(Math.abs(distance) / this.viewport.clientHeight * .35, .3));
      }
    }

    onPointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const pointer = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);
      if (this.pointers.size) {
        if (this.scale > 1.05) {
          const remaining = [...this.pointers.values()][0];
          this.gestureStart = {
            x: remaining.x,
            y: remaining.y,
            time: performance.now(),
            translateX: this.translateX,
            translateY: this.translateY
          };
        }
        this.pinchStart = null;
        return;
      }
      if (!this.gestureStart) return;
      const deltaX = pointer.x - this.gestureStart.x;
      const deltaY = pointer.y - this.gestureStart.y;
      const duration = Math.max(performance.now() - this.gestureStart.time, 1);
      const velocityX = deltaX / duration;
      const velocityY = deltaY / duration;
      if (this.scale > 1 && this.scale <= 1.05) this.zoomTo(1);
      if (this.scale <= 1.05 && this.gestureAxis === 'horizontal') {
        const threshold = this.viewport.clientWidth * .2;
        if (Math.abs(deltaX) >= threshold || Math.abs(velocityX) >= .45) this.go(deltaX < 0 ? 1 : -1);
        else this.animateFrameBack();
      } else if (this.scale <= 1.05 && this.gestureAxis === 'up') {
        if (deltaY <= -90 || velocityY <= -.45) this.animateCloseUp();
        else this.animateSurfaceBack();
      } else if (this.scale <= 1.05 && !this.gestureAxis && Math.hypot(deltaX, deltaY) < 10) {
        this.handleTap(pointer);
      }
      this.gestureStart = null;
      this.gestureAxis = null;
      this.pinchStart = null;
    }

    onPointerCancel(event) {
      this.pointers.delete(event.pointerId);
      if (!this.pointers.size) {
        this.gestureStart = null;
        this.gestureAxis = null;
        this.pinchStart = null;
        this.animateFrameBack();
        this.animateSurfaceBack();
      }
    }

    handleTap(pointer) {
      const now = performance.now();
      if (this.lastTap && now - this.lastTap.time < 280 && Math.hypot(pointer.x - this.lastTap.x, pointer.y - this.lastTap.y) < 25) {
        this.zoomAt(this.scale > 1.05 ? 1 : 2, pointer.x, pointer.y);
        this.lastTap = null;
      } else {
        this.lastTap = { x: pointer.x, y: pointer.y, time: now };
      }
    }

    startPinch() {
      const points = [...this.pointers.values()].slice(0, 2);
      this.pinchStart = {
        distance: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
        centerX: (points[0].x + points[1].x) / 2,
        centerY: (points[0].y + points[1].y) / 2,
        scale: this.scale,
        translateX: this.translateX,
        translateY: this.translateY
      };
      this.gestureAxis = 'pinch';
    }

    updatePinch() {
      if (!this.pinchStart) this.startPinch();
      const points = [...this.pointers.values()].slice(0, 2);
      const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      const centerX = (points[0].x + points[1].x) / 2;
      const centerY = (points[0].y + points[1].y) / 2;
      const nextScale = clamp(this.pinchStart.scale * distance / Math.max(this.pinchStart.distance, 1), 1, 4);
      const rect = this.viewport.getBoundingClientRect();
      const originX = this.pinchStart.centerX - rect.left - rect.width / 2;
      const originY = this.pinchStart.centerY - rect.top - rect.height / 2;
      const ratio = nextScale / this.pinchStart.scale;
      this.translateX = this.pinchStart.translateX + centerX - this.pinchStart.centerX - (originX - this.pinchStart.translateX) * (ratio - 1);
      this.translateY = this.pinchStart.translateY + centerY - this.pinchStart.centerY - (originY - this.pinchStart.translateY) * (ratio - 1);
      this.scale = nextScale;
      this.clampTranslation();
      this.applyZoom();
    }

    zoomAt(nextScale, clientX, clientY) {
      if (this.image.hidden) return;
      const rect = this.viewport.getBoundingClientRect();
      const originX = clientX - rect.left - rect.width / 2;
      const originY = clientY - rect.top - rect.height / 2;
      const ratio = nextScale / this.scale;
      this.translateX = originX - (originX - this.translateX) * ratio;
      this.translateY = originY - (originY - this.translateY) * ratio;
      this.scale = clamp(nextScale, 1, 4);
      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      }
      this.clampTranslation();
      this.applyZoom();
    }

    zoomTo(nextScale) {
      const rect = this.viewport.getBoundingClientRect();
      this.zoomAt(clamp(nextScale, 1, 4), rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    clampTranslation() {
      if (!this.image.naturalWidth || this.scale <= 1) {
        if (this.scale <= 1) {
          this.translateX = 0;
          this.translateY = 0;
        }
        return;
      }
      const viewportWidth = this.viewport.clientWidth;
      const viewportHeight = this.viewport.clientHeight;
      const imageRatio = this.image.naturalWidth / this.image.naturalHeight;
      const viewportRatio = viewportWidth / viewportHeight;
      let baseWidth = viewportWidth;
      let baseHeight = viewportWidth / imageRatio;
      if (imageRatio < viewportRatio) {
        baseHeight = viewportHeight;
        baseWidth = viewportHeight * imageRatio;
      }
      const limitX = Math.max(0, (baseWidth * this.scale - viewportWidth) / 2);
      const limitY = Math.max(0, (baseHeight * this.scale - viewportHeight) / 2);
      this.translateX = clamp(this.translateX, -limitX, limitX);
      this.translateY = clamp(this.translateY, -limitY, limitY);
    }

    applyZoom() {
      this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
    }

    resetZoom() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.image.style.transform = 'translate3d(0, 0, 0) scale(1)';
    }

    animateFrameBack() {
      if (reducedMotion.matches || !this.frame.animate) {
        this.frame.style.transform = 'translate3d(0, 0, 0)';
        this.dragX = 0;
        return;
      }
      const animation = this.frame.animate(
        [
          { transform: `translate3d(${this.dragX}px, 0, 0)` },
          { transform: 'translate3d(0, 0, 0)' }
        ],
        { duration: 180, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );
      animation.finished.catch(() => {}).finally(() => {
        this.frame.style.transform = 'translate3d(0, 0, 0)';
        this.dragX = 0;
      });
    }

    animateSurfaceBack() {
      if (reducedMotion.matches || !this.surface.animate) {
        this.resetGestureStyles();
        return;
      }
      const transform = this.surface.style.transform || 'translate3d(0, 0, 0)';
      const opacity = this.surface.style.opacity || '1';
      const animation = this.surface.animate(
        [
          { transform, opacity },
          { transform: 'translate3d(0, 0, 0)', opacity: 1 }
        ],
        { duration: 200, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );
      animation.finished.catch(() => {}).finally(() => this.resetGestureStyles());
    }

    async animateCloseUp() {
      if (this.animating) return;
      if (reducedMotion.matches || !this.surface.animate) {
        this.close();
        return;
      }
      this.animating = true;
      const transitionSequence = ++this.transitionSequence;
      const transform = this.surface.style.transform || 'translate3d(0, 0, 0)';
      const opacity = this.surface.style.opacity || '1';
      const animation = this.surface.animate(
        [
          { transform, opacity },
          { transform: 'translate3d(0, -100%, 0)', opacity: 0 }
        ],
        { duration: 220, easing: 'cubic-bezier(.4, 0, 1, 1)' }
      );
      await animation.finished.catch(() => {});
      if (transitionSequence !== this.transitionSequence || !this.dialog.open) {
        animation.cancel();
        return;
      }
      animation.cancel();
      this.animating = false;
      this.close();
    }

    resetGestureStyles() {
      this.dragX = 0;
      this.frame.style.transform = 'translate3d(0, 0, 0)';
      this.frame.style.opacity = '1';
      this.surface.style.transform = 'translate3d(0, 0, 0)';
      this.surface.style.opacity = '1';
    }
  }

  class NoyocoInlineProductGallery {
    constructor(gallery, viewer) {
      this.gallery = gallery;
      this.viewer = viewer;
      this.sliderElement = gallery.closest('#main-slider');
      this.root = this.sliderElement.parentElement;
      this.product = gallery.closest('kercambre-product');
      this.lastDrag = 0;
      this.pointerStart = null;
      this.sliderViewport = window.matchMedia('(max-width: 767px)');
      this.sliderEventsBound = false;
      this.root.classList.add('noyoco-product-gallery');
      this.gallery.setAttribute('role', 'group');
      this.gallery.setAttribute('aria-roledescription', 'carousel');
      this.gallery.setAttribute('aria-label', viewer.dataset.galleryLabel);
      this.prepareImages();
      this.prepareCounter();
      this.createControls();
      this.bindEvents();
      this.waitForSlider();
      this.refreshAccessibility();
    }

    prepareImages() {
      this.gallery.querySelectorAll('img').forEach(image => {
        image.draggable = false;
      });
    }

    prepareCounter() {
      this.counter = this.root.querySelector('[splide-index]')?.parentElement;
      if (this.counter) this.counter.classList.add('noyoco-product-gallery__counter');
    }

    createControls() {
      this.controls = document.createElement('div');
      this.controls.className = 'noyoco-product-gallery__controls';
      this.previousButton = this.createArrow('previous', this.viewer.dataset.previousLabel, 'm15 4-8 8 8 8');
      this.nextButton = this.createArrow('next', this.viewer.dataset.nextLabel, 'm9 4 8 8-8 8');
      this.controls.append(this.previousButton, this.nextButton);
      this.root.appendChild(this.controls);
    }

    createArrow(direction, label, path) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `noyoco-product-gallery__arrow noyoco-product-gallery__arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = `<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="${path}" /></svg>`;
      return button;
    }

    bindEvents() {
      this.sliderViewport.addEventListener('change', event => {
        this.sliderEventsBound = false;
        window.requestAnimationFrame(() => {
          if (event.matches) this.waitForSlider();
          else this.refreshAccessibility();
        });
      });
      this.previousButton.addEventListener('click', () => this.move(-1));
      this.nextButton.addEventListener('click', () => this.move(1));
      this.gallery.addEventListener('pointerdown', event => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }, true);
      this.gallery.addEventListener('pointermove', event => {
        if (!this.pointerStart) return;
        if (Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y) > 8) {
          this.lastDrag = performance.now();
        }
      }, true);
      this.gallery.addEventListener('pointerup', () => {
        this.pointerStart = null;
      }, true);
      this.gallery.addEventListener('click', event => {
        if (!mobileViewport.matches) return;
        const anchor = event.target.closest('a');
        if (!anchor || !this.gallery.contains(anchor)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (performance.now() - this.lastDrag < 320) return;
        this.viewer.open(anchor, this.gallery);
      }, true);
      this.gallery.addEventListener('gender-switch', () => {
        window.requestAnimationFrame(() => {
          this.prepareImages();
          this.refreshAccessibility();
          this.sync();
        });
      });
    }

    waitForSlider(attempt = 0) {
      this.slider = this.product?.splide;
      if (!this.slider && attempt < 20) {
        window.setTimeout(() => this.waitForSlider(attempt + 1), 50);
        return;
      }
      if (!this.slider || !this.sliderViewport.matches) return;
      if (!this.sliderEventsBound) {
        ['mounted', 'move', 'moved', 'updated', 'refresh'].forEach(eventName => {
          this.slider.on(eventName, () => this.sync());
        });
        this.slider.on('destroy', () => {
          this.sliderEventsBound = false;
          // Splide restores old slide labels on destroy, including the prior gender count.
          window.requestAnimationFrame(() => this.refreshAccessibility());
        });
        this.sliderEventsBound = true;
      }
      this.removeHiddenSlides();
      this.sync();
    }

    removeHiddenSlides() {
      const elements = this.slider?.Components?.Elements;
      if (!elements || !this.product || !this.sliderViewport.matches) return;
      const slides = [...elements.slides];
      this.product.removedSlides ||= [];
      [...slides].reverse().forEach((slide, reverseIndex) => {
        if (!slide.classList.contains('hidden')) return;
        const html = slide.outerHTML;
        if (!this.product.removedSlides.includes(html)) {
          this.product.removedSlides.push(html);
        }
        this.slider.remove(slides.length - reverseIndex - 1);
      });
    }

    move(delta) {
      if (!this.slider || !mobileViewport.matches) return;
      this.slider.go(clamp(this.slider.index + delta, 0, this.slider.length - 1));
    }

    sync() {
      if (!this.slider) return;
      this.previousButton.disabled = this.slider.index <= 0;
      this.nextButton.disabled = this.slider.index >= this.slider.length - 1;
      this.refreshAccessibility();
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counter) return;
      const indexNode = this.counter.querySelector('[splide-index]');
      const lengthNode = this.counter.querySelector('[splide-length]');
      const total = this.slider?.length || 0;
      const current = total ? clamp(this.slider.index + 1, 1, total) : 0;
      if (indexNode) indexNode.textContent = String(current);
      if (lengthNode) lengthNode.textContent = String(total);
      this.counter.classList.toggle('hidden', total < 2);
    }

    refreshAccessibility() {
      const anchors = [...this.gallery.querySelectorAll(':scope > li:not(.hidden):not(.splide__slide--clone) > a')]
        .filter(anchor => anchor.closest('li')?.offsetParent !== null);
      anchors.forEach((anchor, index) => {
        const position = `${index + 1} / ${anchors.length}`;
        const slide = anchor.closest('li');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', position);
        anchor.setAttribute('aria-label', this.viewer.dataset.openLabel.replace('__index__', position));
      });
      if (!this.slider && this.counter) {
        const indexNode = this.counter.querySelector('[splide-index]');
        const lengthNode = this.counter.querySelector('[splide-length]');
        if (indexNode) indexNode.textContent = anchors.length ? '1' : '0';
        if (lengthNode) lengthNode.textContent = String(anchors.length);
        this.counter.classList.toggle('hidden', anchors.length < 2);
      }
    }
  }

  const initialize = () => {
    const gallery = document.querySelector('#nemoGal');
    const viewer = document.querySelector('noyoco-product-gallery-viewer');
    if (!gallery || !viewer || gallery.dataset.noyocoGalleryReady === 'true') return;
    gallery.dataset.noyocoGalleryReady = 'true';
    new NoyocoInlineProductGallery(gallery, viewer);
  };

  if (!customElements.get('noyoco-product-gallery-viewer')) {
    customElements.define('noyoco-product-gallery-viewer', NoyocoProductGalleryViewer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
